/** Hosts the statistics dashboard and dispatches KPI drilldowns into actionable brand surfaces. */
import * as React from "react";
import {FC, useCallback, useState} from "react";
import {Alert, Box, LinearProgress} from "@mui/material";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin, CurrentUserState} from "../../atoms/CurrentUser";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {ParamBrandHouse} from "../../Parameters";
import {useDashboardPageBrandQuery} from "../../Queries";
import {DataType, getStatisticsDashboards, StatisticsDashboard} from "@michaeltoft/c2s-statistics-dashboard";
import type {DrilldownResult} from "@michaeltoft/c2s-statistics-dashboard";
import {
   classifyDashboardDrilldown
} from "./dashboardDrilldown/DashboardDrilldownRouting";
import {BrandItemDrilldownWorkspace} from "./dashboardDrilldown/BrandItemDrilldownWorkspace";
import {DashboardDrilldownDestinations} from "./dashboardDrilldown/DashboardDrilldownDestinations";

/** Merges the dashboard package's history-managed tab into React Router query state. */
const currentDashboardSearchParams = (routerParams: URLSearchParams): URLSearchParams => {
   const next = new URLSearchParams(routerParams);
   const browserParams = new URLSearchParams(window.location.search);
   const activeTab = browserParams.get("tab");
   if (activeTab) {
      next.set("tab", activeTab);
   } else {
      next.delete("tab");
   }
   return next;
};

/** Renders the dashboard or its URL-backed brand-item cohort workspace. */
export const DashboardPage: FC = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>();
   const navigate = useNavigate();
   const [searchParams, setSearchParams] = useSearchParams();
   const currentUser = useRecoilValue(CurrentUserState);
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);
   const [relatedViews, setRelatedViews] = useState<DrilldownResult[]>([]);
   const [destinationChoices, setDestinationChoices] = useState<DrilldownResult[]>([]);
   const [drilldownError, setDrilldownError] = useState<string>();
   const {data} = useDashboardPageBrandQuery({
      variables: {
         id: brandHouseId
      }
   });

   /** Opens a fallback list or a brand-item view selected from the chooser. */
   const openDestination = useCallback((target: DrilldownResult): void => {
      setDestinationChoices([]);
      setDrilldownError(undefined);
      if (target.dataType === DataType.Branditems) {
         setRelatedViews([]);
         const next = currentDashboardSearchParams(searchParams);
         next.set("drilldownViewId", target._id);
         setSearchParams(next);
         return;
      }
      if (!target.url) {
         setDrilldownError("No destination is available for this drilldown");
         return;
      }
      const destination = new URL(target.url, window.location.origin);
      if (destination.origin === window.location.origin) {
         navigate(`${destination.pathname}${destination.search}${destination.hash}`);
      } else {
         window.location.assign(target.url);
      }
   }, [navigate, searchParams, setSearchParams]);

   /** Dispatches every package result without losing multi-view drilldowns. */
   const handleDrilldown = useCallback((results: DrilldownResult[]): void => {
      const intent = classifyDashboardDrilldown(results);
      setDrilldownError(undefined);
      if (intent.kind === "empty") {
         setDrilldownError("No drilldown data was returned for this figure");
         return;
      }
      if (intent.kind === "choose") {
         setDestinationChoices(intent.targets);
         return;
      }
      if (intent.kind === "navigate") {
         openDestination(intent.target);
         return;
      }

      setRelatedViews(intent.related);
      const next = currentDashboardSearchParams(searchParams);
      next.set("drilldownViewId", intent.primary._id);
      setSearchParams(next);
   }, [openDestination, searchParams, setSearchParams]);

   /** Returns to the dashboard while preserving unrelated query parameters. */
   const closeWorkspace = useCallback((): void => {
      const next = currentDashboardSearchParams(searchParams);
      next.delete("drilldownViewId");
      setSearchParams(next, {replace: true});
      setRelatedViews([]);
   }, [searchParams, setSearchParams]);

   const drilldownViewId = searchParams.get("drilldownViewId");
   if (drilldownViewId) {
      return <BrandItemDrilldownWorkspace
         brandHouseId={brandHouseId || ""}
         viewId={drilldownViewId}
         relatedViews={relatedViews}
         onClose={closeWorkspace}
      />;
   }

   if (!data?.BrandHouse) {
      return <LinearProgress/>
   }

   const currency = (data.BrandHouse.shops?.find(s => s.liveDate) || data.BrandHouse.shops?.[0])?.currency || 'DKK';

   return <>
      {drilldownError && <Box sx={{mb: 2}}>
         <Alert severity="error" onClose={() => setDrilldownError(undefined)}>{drilldownError}</Alert>
      </Box>}
      <StatisticsDashboard
         accessToken={currentUser?.token || ''}
         graphqlUrl="https://statistics.create2stay.com/api/graphql"
         brandHouseId={brandHouseId || ''}
         currency={currency}
         dashboards={getStatisticsDashboards(isAdmin)}
         onDrilldown={handleDrilldown}
      />
      <DashboardDrilldownDestinations
         targets={destinationChoices}
         onClose={() => setDestinationChoices([])}
         onOpen={openDestination}
      />
   </>
}
